import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Hook to manage page transitions and prevent scroll position conflicts
 * This hook ensures clean transitions between pages and prevents
 * content overlap issues during navigation
 */
export const usePageTransition = () => {
  const location = useLocation();

  // Use layoutEffect for immediate DOM changes before paint
  useLayoutEffect(() => {
    // 0. Temporarily disable smooth scrolling to ensure instant reset
    const htmlElement = document.documentElement;
    const originalScrollBehavior = htmlElement.style.scrollBehavior;
    htmlElement.style.scrollBehavior = 'auto';

    // 1. Immediately reset all scroll positions to prevent visual artifacts
    window.scrollTo({ top: 0, behavior: 'instant' });

    // 2. Reset all scroll containers
    const scrollContainers = document.querySelectorAll('.custom-scroll');
    scrollContainers.forEach((container) => {
      if (container instanceof HTMLElement) {
        const originalBehavior = container.style.scrollBehavior;
        container.style.scrollBehavior = 'auto';
        container.scrollTo({ top: 0, behavior: 'instant' });
        // Restore after a frame
        requestAnimationFrame(() => {
          container.style.scrollBehavior = originalBehavior;
        });
      }
    });

    // 3. Reset main content container
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.scrollTo({ top: 0, behavior: 'instant' });
      // Ensure content is fully visible after navigation
      mainContent.style.opacity = '1';
      mainContent.style.transition = 'opacity 0.2s ease-out';
    }

    // 4. Force repaint for header elements to prevent overlap
    const headerElements = document.querySelectorAll('[class*="header"]');
    headerElements.forEach((header) => {
      if (header instanceof HTMLElement) {
        header.style.transform = 'translateZ(0)';
        header.style.willChange = 'transform';
      }
    });

    // 5. Restore original scroll behavior after reset
    requestAnimationFrame(() => {
      htmlElement.style.scrollBehavior = originalScrollBehavior;
    });

    return () => {
      const currentMainContent = document.getElementById('main-content');
      if (currentMainContent) {
        // Ensure opacity is restored if unmounted during transition
        currentMainContent.style.opacity = '1';
        currentMainContent.style.transition = 'opacity 0.2s ease-out';
      }
    };
  }, [location.pathname]);

  return location.pathname;
};
